import React, { useState, useRef, useEffect } from 'react';

/**
 * TapToTypeValue - A value display that looks like plain text until tapped
 * On tap/click: becomes editable with subtle underline
 * On blur/Enter: validates, clamps to min/max, calls onChange
 * 
 * Props:
 * - value: current numeric value
 * - onChange: callback with new value
 * - min: minimum allowed value
 * - max: maximum allowed value
 * - prefix: string prefix like '$' (default: '')
 * - suffix: string suffix like '%' (default: '')
 * - decimals: number of decimal places (default: 0)
 * - formatCommas: whether to format with commas (default: true)
 */
export default function TapToTypeValue({
  value,
  onChange,
  min = 0,
  max = 100000,
  prefix = '',
  suffix = '',
  decimals = 0,
  formatCommas = true,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);
  const previousValue = useRef(value);

  // Format the display value
  const formatValue = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    let formatted;
    if (decimals > 0) {
      formatted = num.toFixed(decimals);
    } else {
      formatted = formatCommas ? Math.round(num).toLocaleString() : Math.round(num).toString();
    }
    return `${prefix}${formatted}${suffix}`;
  };

  // Handle click to start editing
  const handleClick = () => {
    previousValue.current = value;
    setIsEditing(true);
    // Set raw numeric value for editing (no prefix/suffix/commas)
    if (decimals > 0) {
      setEditValue(value.toFixed(decimals));
    } else {
      setEditValue(value.toString());
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setEditValue(e.target.value);
  };

  // Validate and apply the new value
  const applyValue = () => {
    // Strip everything except digits, decimal point, and minus sign
    const raw = editValue.replace(/[^0-9.-]/g, '');
    const num = parseFloat(raw);
    
    if (!isNaN(num)) {
      // Clamp to min/max
      const clamped = Math.min(Math.max(num, min), max);
      onChange(clamped);
    } else {
      // Revert to previous value if invalid
      onChange(previousValue.current);
    }
    setIsEditing(false);
  };

  // Handle blur
  const handleBlur = () => {
    applyValue();
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      applyValue();
    } else if (e.key === 'Escape') {
      // Cancel editing, revert to previous value
      setIsEditing(false);
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={editValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="slider-tap-value"
        style={{
          fontFamily: 'inherit',
        }}
      />
    );
  }

  return (
    <span
      onClick={handleClick}
      className="slider-tap-value"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      style={{
        display: 'inline-block',
        cursor: 'text',
      }}
    >
      {formatValue(value)}
    </span>
  );
}
